"use client"

import { useRouter } from "next/navigation"
import { Header } from "@/components/landrisk/header"
import { Footer } from "@/components/landrisk/footer"
import { StepIndicator } from "@/components/landrisk/step-indicator"
import { useLandRisk } from "@/lib/landrisk-context"
import type { PackageTier } from "@/lib/types"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import {
  Check,
  MapPin,
  Droplets,
  Scale,
  Mountain,
  ScanSearch,
  TrendingUp,
  Leaf,
  Box,
  Star,
  AlertCircle,
} from "lucide-react"
import { useEffect, useState } from "react"

const packages = [
  {
    id: "basic" as PackageTier,
    name: "Basic",
    priceUSD: 19,
    priceKES: 2500,
    description: "Essential risk checks for confident land buying decisions",
    features: [
      { label: "Flood & riparian risk scoring", icon: Droplets, included: true },
      { label: "Satellite imagery overlay", icon: ScanSearch, included: true },
      { label: "Basic legal red-flag scan", icon: Scale, included: true },
      { label: "Slope & terrain assessment", icon: Mountain, included: true },
      { label: "Manual valuation logic", icon: TrendingUp, included: false },
      { label: "Environmental index", icon: Leaf, included: false },
      { label: "3D terrain view", icon: Box, included: false },
    ],
    recommended: false,
  },
  {
    id: "pro" as PackageTier,
    name: "Pro",
    priceUSD: 59,
    priceKES: 7500,
    description: "Comprehensive analysis for professionals and serious investors",
    features: [
      { label: "Flood & riparian risk scoring", icon: Droplets, included: true },
      { label: "Satellite imagery overlay", icon: ScanSearch, included: true },
      { label: "Comprehensive legal scan", icon: Scale, included: true },
      { label: "Slope & terrain assessment", icon: Mountain, included: true },
      { label: "Manual valuation logic", icon: TrendingUp, included: true },
      { label: "Environmental index", icon: Leaf, included: true },
      { label: "3D terrain view", icon: Box, included: true },
    ],
    recommended: true,
  },
]

export default function PricingPage() {
  const router = useRouter()
  const { location, setPackageTier, setLocationConfirmed } = useLandRisk()
  const [ready, setReady] = useState(false)
  const [locationConfirm, setLocationConfirm] = useState(false)
  const [exchangeRate, setExchangeRate] = useState<number | null>(null)

  const formatUsd = (value: number) => `$${value.toFixed(0)}`
  const formatKes = (value: number) => `KES ${Math.round(value).toLocaleString()}`

  useEffect(() => {
    // Fetch exchange rate from xe.com API or use a default
    const fetchExchangeRate = async () => {
      try {
        // Using a public exchange rate API as fallback
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD')
        const data = await response.json()
        setExchangeRate(data.rates.KES || 130) // Default to 130 if API fails
      } catch (error) {
        setExchangeRate(130) // Default exchange rate
      }
    }
    fetchExchangeRate()
  }, [])

  useEffect(() => {
    // Wait a tick for sessionStorage hydration before checking
    const timer = setTimeout(() => {
      setReady(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (ready && !location) {
      router.replace("/identify")
    }
  }, [ready, location, router])

  if (!ready || !location) return null

  const promoMultiplier = 0.5

  const handleSelect = (tier: PackageTier) => {
    if (!locationConfirm) {
      return
    }
    setPackageTier(tier)
    setLocationConfirmed(true)
    router.push("/payment")
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 bg-muted/20">
        <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
          <div className="mb-8">
            <StepIndicator currentStep={2} />
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Select Your Package
            </h1>
            <p className="mt-2 text-muted-foreground">
              Choose the level of analysis for your land assessment
            </p>
          </div>

          <Card className="mb-8 border-dashed">
            <CardContent className="flex flex-col gap-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin className="size-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {location.areaName}, {location.county} County
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
                <Checkbox
                  id="confirm-location"
                  checked={locationConfirm}
                  onCheckedChange={(checked) => setLocationConfirm(checked as boolean)}
                />
                <label
                  htmlFor="confirm-location"
                  className="text-sm text-foreground cursor-pointer leading-relaxed"
                >
                  I confirm this is the correct parcel location and wish to proceed with the analysis
                </label>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col items-stretch gap-6 md:flex-row">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={cn(
                  "relative flex flex-1 flex-col transition-shadow hover:shadow-lg",
                  pkg.recommended && "border-primary shadow-md ring-1 ring-primary/20"
                )}
              >
                <div className="absolute left-4 top-4">
                  <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                    50% off next month
                  </span>
                </div>
                {pkg.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                      <Star className="size-3" />
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-xl">{pkg.name}</CardTitle>
                  <CardDescription>{pkg.description}</CardDescription>
                  <div className="mt-4">
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-sm text-muted-foreground line-through">
                        {formatUsd(pkg.priceUSD)}
                      </span>
                      <span className="text-3xl font-bold text-foreground">
                        {formatUsd(pkg.priceUSD * promoMultiplier)}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground"> / report</span>
                  </div>
                  {exchangeRate && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      ≈ <span className="line-through">{formatKes(pkg.priceKES)}</span>{" "}
                      <span className="font-medium text-foreground">
                        {formatKes(pkg.priceKES * promoMultiplier)}
                      </span>
                    </p>
                  )}
                </CardHeader>
                <CardContent className="flex flex-1 flex-col">
                  <ul className="flex flex-1 flex-col gap-3">
                    {pkg.features.map((feature) => (
                      <li
                        key={feature.label}
                        className={cn(
                          "flex items-center gap-2.5 text-sm",
                          feature.included ? "text-foreground" : "text-muted-foreground/50"
                        )}
                      >
                        {feature.included ? (
                          <Check className="size-4 shrink-0 text-primary" />
                        ) : (
                          <div className="size-4 shrink-0" />
                        )}
                        <feature.icon className="size-4 shrink-0" />
                        <span className={cn(!feature.included && "line-through")}>
                          {feature.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSelect(pkg.id)}
                    variant={pkg.recommended ? "default" : "outline"}
                    size="lg"
                    className="mt-6 w-full"
                    disabled={!locationConfirm}
                  >
                    {pkg.recommended ? "Get Pro Report at 50% Off" : "Get Basic Report at 50% Off"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Payment is simulated for this demo. In production, you would be
            redirected to a secure payment gateway.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  )
}
